/* iter55u — AppShell now delegates dialog wiring to `AppDialogs.jsx`
 * and the loader splash to `AppLoadingSplash.jsx`. Was 210 LOC/CC 29;
 * now under 130 LOC.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { NewProjectDialog } from './dialogs/NewProjectDialog';
import SaveWorkDialog from './dialogs/SaveWorkDialog';
import { TitleBar } from './chrome/TitleBar';
import { MenuBar } from './chrome/MenuBar';
import { ActivityBar } from './chrome/ActivityBar';
import { StatusBar } from './chrome/StatusBar';
import { STATUSBAR_LS_KEY, STATUSBAR_EVENT } from './chrome/StatusBar';
import { Workspace } from './workspace/Workspace';
import TrialBanner from './TrialBanner';
import AdminModeBanner from './AdminModeBanner';
import BonusToolsBanner from './BonusToolsBanner';   // Feb-2026 revenue lifts
import OnboardingPromptModeDialog from './OnboardingPromptModeDialog';
import { getPromptMode } from './OnboardingPromptModeDialog';
import GettingStartedWizard from './dialogs/GettingStartedWizard';   // Feb-2026
import CreationJourneyCard  from './CreationJourneyCard';              // Step-by-step creation walkthrough
import { CommandPaletteHint } from './chrome/CommandPaletteHint';
import AppDialogs from './AppDialogs';
import AppLoadingSplash from './AppLoadingSplash';
import ModeSwitchHandler from './ModeSwitchHandler';
import { ChatDock } from './chat/ChatDock';
import QuickstartGuide from './QuickstartGuide';
import BusinessCenterDrawer from './business/BusinessCenterDrawer';
import { usePW } from '@/lib/store';
import { useAuth } from '@/lib/auth_context';
import { usePaymentReturn } from '@/lib/usePaymentReturn';
import { useAudiobookUnlockReturn } from '@/lib/useAudiobookUnlockReturn';
import { useAudiobookProductionReturn, useCanvaCoverPurchaseReturn, useEditorialOrderReturn } from '@/lib/useStripeReturn';
import StripeReturnBanner from '@/components/StripeReturnBanner';
import { installBibleFieldFocus } from '@/lib/bibleFieldFocus';   // iter-studio-dashboard-3
import { useLoomPresence } from '@/hooks/useLoomPresence';
import OpportunityBanner from './OpportunityBanner';
import FreeTierSunsetBanner from './FreeTierSunsetBanner';
import SuggestionBoxWidget from './SuggestionBoxWidget';
import ContextHelpDrawer from './ContextHelpDrawer';   // iter-context-help
import MilestoneToastEngine from './MilestoneToastEngine'; // iter-milestones
import OnboardingActivationWizard from './OnboardingActivationWizard'; // PLG onboarding
import MarketingAssetGenerator from './MarketingAssetGenerator'; // Marketing assets
import TokenNudgeBanner from './TokenNudgeBanner';   // Feb-2026 token cap nudge
import TokenCapGate from './TokenCapGate';             // Feb-2026 full-screen cap gate
import WritingCoachCard from './WritingCoachCard';     // 8-phase Writing Coach guided card
import ManuscriptAiImportDialog from './dialogs/ManuscriptAiImportDialog'; // Drag-drop AI import
import RenameManuscriptDialog from './dialogs/RenameManuscriptDialog'; // File → Rename
import AdminProjectPickerDialog from './dialogs/AdminProjectPickerDialog'; // Admin → Open Any Project
import OnboardingFileMenuPopup, { useOnboardingGlow } from './OnboardingFileMenuGlow'; // Guided-mode glow
import { WorkflowRibbon } from './panels/editor/WorkflowRibbon';
import { CommitGateDialog, isAct1Committed } from './dialogs/CommitGateDialog';
import { DriftDetectionBanner } from './DriftDetectionBanner';
// Install once at module load — safe: guarded internally.
installBibleFieldFocus();

// iter-48 — Default panel visibility. Overridden by localStorage after
// the first render so returning users get their chosen layout back.
const DEFAULT_VISIBLE = {
  // Feb-2026 · Task Launcher is the front door for a fresh browser
  // session (no saved `pw_panel_visibility` yet) — one click away from
  // the editor either way, via its own "Write my next chapter" card or
  // the Manuscript icon in the ActivityBar.
  home: true, editor: false, chapters: false, modes: false, variables: false,
  bible: true,  arc: false,      chat: true,   analysis: false,
  marketing: false, community: false, bookshelf: false, editorial: false,
  deviation: false, plotex: false, worldex: false,
};

function loadVisibility() {
  try {
    const raw = window.localStorage.getItem('pw_panel_visibility');
    if (raw) return { ...DEFAULT_VISIBLE, ...JSON.parse(raw) };
  } catch { /* localStorage blocked */ }
  return DEFAULT_VISIBLE;
}

// ── First-novel prompt ─────────────────────────────────────────────────────
// Shown when a brand-new user opens /app and has no manuscripts yet.
// Guides them into NewProjectDialog so they start with their own
// project instead of a pre-seeded "The Door at the End of Maps".
// Shown when the user already has projects but the bootstrap failed to load
// one — e.g. a transient 5xx during the projects-list call on re-mount.
// This is NOT the new-user onboarding screen; it's a recovery surface.
function ReconnectScreen() {
  const [retrying, setRetrying] = React.useState(false);
  const handleRetry = async () => {
    setRetrying(true);
    // A hard reload re-runs the full bootstrap with a fresh network request.
    window.location.reload();
  };
  return (
    <div className="flex h-full w-full flex-col items-center justify-center gap-4 bg-background p-8 text-center">
      <div className="text-[28px]">⟳</div>
      <p className="text-[15px] font-semibold text-foreground">Reconnecting to your workspace</p>
      <p className="max-w-xs text-[13px] text-muted-foreground">
        Your projects exist but could not be loaded right now — this is usually a temporary server
        hiccup. Reload to try again.
      </p>
      <button
        onClick={handleRetry}
        disabled={retrying}
        className="mt-2 rounded-md bg-primary px-5 py-2 text-[13px] font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
      >
        {retrying ? 'Reloading…' : 'Reload workspace'}
      </button>
    </div>
  );
}

function FirstNovelPrompt() {
  const [open, setOpen] = useState(false);
  return (
    <div className="flex h-full w-full flex-col items-center justify-center bg-background text-foreground p-8">
      <div className="flex flex-col items-center gap-6 text-center max-w-sm">
        {/* Branded icon */}
        <div className="flex h-16 w-16 items-center justify-center rounded-xl bg-gradient-to-br from-primary to-primary/70 text-primary-foreground shadow-lg">
          <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round">
            <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>
          </svg>
        </div>

        <div className="space-y-2">
          <h1 className="text-2xl font-semibold tracking-tight">Your story begins here</h1>
          <p className="text-sm text-muted-foreground leading-relaxed">
            The Loom is your AI-assisted writing workspace. Create your first novel to get started.
          </p>
        </div>

        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid="first-novel-create-btn"
          className="inline-flex items-center gap-2 rounded-lg bg-primary px-6 py-2.5 text-[14px] font-semibold text-primary-foreground shadow-md transition hover:brightness-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          Create my first novel
        </button>

        <p className="text-[11px] text-muted-foreground/60">
          Already have a manuscript?&nbsp;You&apos;ll be able to import it right after.
        </p>
      </div>

      <NewProjectDialog open={open} onOpenChange={setOpen} />
    </div>
  );
}

export function AppShell() {
  const { loading, project, projects, chapters, activeMode, modes } = usePW();
  const { refresh } = useAuth();
  const [dialogs, setDialogs] = useState({
    settings: false, about: false, newProject: false,
    tokens: false, export: false, importManuscript: false, importSynopsis: false,
    publish: false, royalties: false, help: false, templates: false,
    bookCoaching: false, requestEditorReview: false, audiobook: false,
    // iter55g24 — mode-switch UX
    worldBuilder: false, revisionMode: false,
    // screenplay / teleplay converter
    scriptConverter: false,
    // KDP metadata form
    kdpMetadata: false,
  });
  const [pendingSynopsisFile, setPendingSynopsisFile] = useState(null); // dropped .docx pre-load
  const [viewingReviewId, setViewingReviewId] = useState(null);
  const [businessCenterOpen, setBusinessCenterOpen] = useState(false);
  const [saveWorkOpen, setSaveWorkOpen] = useState(false);  // gate before New Project
  const [helpDrawerOpen, setHelpDrawerOpen] = useState(false); // iter-context-help
  const [adminPickerOpen, setAdminPickerOpen] = useState(false); // admin → open any project
  const [renameManuscriptOpen, setRenameManuscriptOpen] = useState(false); // File → Rename Manuscript
  const [commitGateOpen, setCommitGateOpen] = useState(false);  // Act I Commit Gate
  const [statusBarVisible, setStatusBarVisible] = useState(
    () => localStorage.getItem(STATUSBAR_LS_KEY) !== '0',
  );
  const [marketingAssetsOpen, setMarketingAssetsOpen] = useState(false); // PLG
  // ── Quiet Mode: hides TrialBanner, BonusToolsBanner, OpportunityBanner ──
  const [bannersQuiet, setBannersQuiet] = useState(
    () => localStorage.getItem('loom_banners_quiet') !== '0',
  );
  const [visible, setVisible] = useState(loadVisibility);
  // Feb-2026 — most-recently-opened panel, tracked by togglePanel below.
  const lastFocusedPanelRef = useRef(null);
  // Feb-2026 · Simple Mode — one toolbar click hides technical/power
  // tools (Plagiarism, Cross-post, Advanced Modeling, bulk/batch, Tools
  // menu) for writers who just want a focused writing surface.
  const [simpleMode, setSimpleModeState] = useState(() => {
    try { return window.localStorage.getItem('pw_simple_mode') === '1'; }
    catch { return false; }
  });
  const setSimpleMode = (on) => {
    setSimpleModeState(on);
    try { window.localStorage.setItem('pw_simple_mode', on ? '1' : '0'); } catch { /* ignore */ }
    if (on) {
      // Clean baseline — don't leave the technical panels running
      // behind a menu that's no longer there to turn them back off.
      setVisible((v) => ({ ...v, variables: false, arc: false, advChar: false }));
    }
  };
  const currentArea = useMemo(() => {
    const openDialog = Object.keys(dialogs).find((key) => dialogs[key]);
    if (businessCenterOpen) return 'Business Center';
    if (openDialog) return openDialog.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase());
    const lastFocused = lastFocusedPanelRef.current;
    const focusedPanel = (lastFocused && visible[lastFocused]) ? lastFocused
      : ['analysis', 'marketing', 'bookshelf', 'community', 'chat', 'bible', 'chapters', 'modes', 'variables', 'arc', 'plotex', 'worldex']
          .find((key) => visible[key]);
    if (focusedPanel) return focusedPanel === 'bible' ? 'Character Bible' : focusedPanel.replace(/^./, (c) => c.toUpperCase());
    const mode = modes.find((item) => item.id === activeMode);
    return `Editor · ${mode?.title || 'Draft'}`;
  }, [activeMode, businessCenterOpen, dialogs, modes, visible]);
  useLoomPresence(currentArea);
  // Feb-2026 — Contextual AI Help: expose the raw panel KEY (not the
  // human label above) on `window` so the globally-mounted
  // <AiHelpDialog/> (outside this component's provider tree) can read
  // "what is the author looking at right now" without prop-drilling.
  // Prefers the panel the author most recently opened (lastFocusedPanelRef,
  // updated by togglePanel below) over a fixed priority scan, so e.g.
  // opening Bible while Chat is already open correctly reports "bible".
  const currentPanelKey = useMemo(() => {
    const openDialog = Object.keys(dialogs).find((key) => dialogs[key]);
    if (openDialog) return openDialog;
    const lastFocused = lastFocusedPanelRef.current;
    if (lastFocused && visible[lastFocused]) return lastFocused;
    const focusedPanel = ['analysis', 'marketing', 'bookshelf', 'community', 'chat', 'bible', 'chapters', 'modes', 'variables', 'arc', 'plotex', 'worldex']
      .find((key) => visible[key]);
    return focusedPanel || 'editor';
  }, [dialogs, visible]);
  useEffect(() => {
    try { window.__loomCurrentPanel = currentPanelKey; } catch { /* ignore */ }
    return () => { try { delete window.__loomCurrentPanel; } catch { /* ignore */ } };
  }, [currentPanelKey]);
  // iter55f93 — Quickstart Guide open state. Persisted in localStorage
  // so a pinned guide stays open across reloads.
  const [quickstartOpen, setQuickstartOpen] = useState(() => {
    try { return window.localStorage.getItem('pw_quickstart_open') === '1'; }
    catch { return false; }
  });
  useEffect(() => {
    try { window.localStorage.setItem('pw_quickstart_open', quickstartOpen ? '1' : '0'); }
    catch { /* ignore */ }
  }, [quickstartOpen]);
  // Sync bannersQuiet when ToolsMenu (or any tab) changes the loom_banners_quiet key
  useEffect(() => {
    const handler = () => setBannersQuiet(localStorage.getItem('loom_banners_quiet') !== '0');
    window.addEventListener('loom:banners-quiet', handler);
    // also catch cross-tab changes via storage event
    const storageHandler = (e) => e.key === 'loom_banners_quiet' && handler();
    window.addEventListener('storage', storageHandler);
    return () => {
      window.removeEventListener('loom:banners-quiet', handler);
      window.removeEventListener('storage', storageHandler);
    };
  }, []);

  // Sync statusBarVisible when Preferences toggle or dismiss button fires
  useEffect(() => {
    const handler = () => setStatusBarVisible(localStorage.getItem(STATUSBAR_LS_KEY) !== '0');
    window.addEventListener(STATUSBAR_EVENT, handler);
    const storageHandler = (e) => e.key === STATUSBAR_LS_KEY && handler();
    window.addEventListener('storage', storageHandler);
    return () => {
      window.removeEventListener(STATUSBAR_EVENT, handler);
      window.removeEventListener('storage', storageHandler);
    };
  }, []);

  // Admin project picker
  useEffect(() => {
    const handler = () => setAdminPickerOpen(true);
    window.addEventListener('loom:open-admin-project-picker', handler);
    return () => window.removeEventListener('loom:open-admin-project-picker', handler);
  }, []);
  const [aiImportOpen, setAiImportOpen] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [stripeReturnBanner, setStripeReturnBanner] = useState(null);
  const nav = useNavigate();

  // Handle Stripe checkout return: refresh the user (to pick up the new token balance)
  // when the polling hook confirms the payment is paid.
  usePaymentReturn(refresh);
  // 2026-10 — Audiobook unlock checkout return: reopen the dialog so the
  // author immediately sees narration resuming on the now-unlocked job.
  useAudiobookUnlockReturn(() => open('audiobook'));
  // Feb-2026 — Audiobook production purchase return
  useAudiobookProductionReturn(() => open('audiobook'), setStripeReturnBanner);
  // Feb-2026 — Canva cover purchase return
  useCanvaCoverPurchaseReturn(() => open('canva'), setStripeReturnBanner);
  // Feb-2026 — Editorial Services order return
  useEditorialOrderReturn(() => togglePanel('editorial', true), setStripeReturnBanner);

  const open  = (k) => setDialogs((s) => ({ ...s, [k]: true  }));
  const close = (k) => setDialogs((s) => ({ ...s, [k]: false }));

  // ── New-project guard — shows SaveWorkDialog when a project is loaded ──
  const handleNewProject = React.useCallback(() => {
    if (project) {
      setSaveWorkOpen(true);
    } else {
      open('newProject');
    }
  }, [project]); // eslint-disable-line react-hooks/exhaustive-deps

  // iter55x — First-run onboarding moved to WelcomeOverlay (data-driven
  // by "project is genuinely empty"). Removes the older auto-open of
  // the community TemplatesPicker so we don't stack two modals for a
  // brand-new user.

  // iter-39 — Global F1 shortcut always opens Help.
  // 2026-10 — also listen for the 'loom:open-help-manual' event fired by
  // the small "Manual" pill next to the floating AI-help button.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'F1') return;
      e.preventDefault();
      open('help');
    };
    const onManualEvent = () => open('help');
    const onPublishEvent = () => open('publish');
    const onMarketingAssetsEvent = () => setMarketingAssetsOpen(true);
    const onOpenCharacterBible = () => togglePanel('bible', true);
    const onOpenSynopsis = () => open('buildSynopsis');
    const onOpenAiImport = () => setAiImportOpen(true);
    const onOpenNarration = () => open('narrationStudio');
    const onOpenBulkManuscripts = () => open('bulkManuscripts');
    const onOpenRenameManuscript = () => setRenameManuscriptOpen(true);
    window.addEventListener('keydown', onKey);
    window.addEventListener('loom:open-help-manual', onManualEvent);
    window.addEventListener('loom:open-publish', onPublishEvent);
    window.addEventListener('loom:open-marketing-assets', onMarketingAssetsEvent);
    window.addEventListener('loom:open-character-bible', onOpenCharacterBible);
    window.addEventListener('loom:open-synopsis', onOpenSynopsis);
    window.addEventListener('loom:open-ai-import', onOpenAiImport);
    window.addEventListener('loom:open-narration', onOpenNarration);
    window.addEventListener('loom:open-bulk-manuscripts', onOpenBulkManuscripts);
    window.addEventListener('loom:open-manuscript-rename', onOpenRenameManuscript);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('loom:open-help-manual', onManualEvent);
      window.removeEventListener('loom:open-publish', onPublishEvent);
      window.removeEventListener('loom:open-marketing-assets', onMarketingAssetsEvent);
      window.removeEventListener('loom:open-character-bible', onOpenCharacterBible);
      window.removeEventListener('loom:open-synopsis', onOpenSynopsis);
      window.removeEventListener('loom:open-ai-import', onOpenAiImport);
      window.removeEventListener('loom:open-narration', onOpenNarration);
      window.removeEventListener('loom:open-bulk-manuscripts', onOpenBulkManuscripts);
      window.removeEventListener('loom:open-manuscript-rename', onOpenRenameManuscript);
    };
  }, []);

  // Commit Gate — intercept "loom:draft-requested" from WorkflowRibbon Draft button.
  // If Act I is already committed, skip the gate and open editor directly.
  useEffect(() => {
    const handler = () => {
      const pid = project?.id;
      if (pid && isAct1Committed(pid)) {
        // Already committed — go straight to editor
        setVisible((v) => ({ ...v, editor: true, home: false }));
      } else {
        setCommitGateOpen(true);
      }
    };
    window.addEventListener('loom:draft-requested', handler);
    return () => window.removeEventListener('loom:draft-requested', handler);
  }, [project?.id]);

  // Also listen for generic loom:open-panel events (from various places)
  useEffect(() => {
    const handler = (e) => {
      const key = e?.detail;
      if (key) setVisible((v) => ({ ...v, [key]: true }));
    };
    window.addEventListener('loom:open-panel', handler);
    return () => window.removeEventListener('loom:open-panel', handler);
  }, []);

  // Persist visibility on change.
  useEffect(() => {
    try { window.localStorage.setItem('pw_panel_visibility', JSON.stringify(visible)); }
    catch { /* ignore */ }
  }, [visible]);

  const togglePanel = (key, value) => {
    const next = value !== undefined ? value : !visible[key];
    if (next) lastFocusedPanelRef.current = key;   // Feb-2026 — track the panel the author actually just opened
    setVisible((v) => ({ ...v, [key]: next }));
  };

  // ── Empty-manuscript hint ────────────────────────────────────────────────────
  // Shows a callout pointing to File → Load Manuscript when the project has no
  // prose yet. Dismissed via localStorage flag (persists per browser).
  const hasManuscriptContent = chapters.some((c) => (c.content || '').trim().length > 0 || c.words > 0);
  const [hintDismissed, setHintDismissed] = useState(
    () => !!localStorage.getItem('loom:import-hint-dismissed'),
  );
  const dismissImportHint = () => {
    setHintDismissed(true);
    try { localStorage.setItem('loom:import-hint-dismissed', '1'); } catch { /* blocked */ }
  };
  const showImportHint = project && !hasManuscriptContent && !hintDismissed;

  // ── Onboarding Guided Glow ────────────────────────────────────────────────
  // Tracks which prompt mode the user chose so we can react to same-session
  // changes (e.g. first pick of "Guided") not just persistence from prev session.
  const [promptMode, setPromptModeLocal] = useState(getPromptMode);
  useEffect(() => {
    const handler = (e) => setPromptModeLocal(e.detail?.mode || getPromptMode());
    window.addEventListener('loom:prompt-mode-changed', handler);
    return () => window.removeEventListener('loom:prompt-mode-changed', handler);
  }, []);
  const isGlowActive = !!(project && !hasManuscriptContent && promptMode === 'guided');
  const { glowStep, advance: advanceGlow } = useOnboardingGlow(isGlowActive);

  const glowFileTrigger = isGlowActive && (glowStep === 'file-menu' || glowStep === 'load-manuscript');
  const glowLoadManuscript = isGlowActive && glowStep === 'load-manuscript';

  const handleFileMenuOpen = useCallback(() => {
    if (glowStep === 'file-menu') advanceGlow();
  }, [glowStep, advanceGlow]);

  const handleLoadManuscriptClicked = useCallback(() => {
    if (glowStep === 'load-manuscript') advanceGlow();
  }, [glowStep, advanceGlow]);

  if (loading) return <AppLoadingSplash />;

  // No projects yet — guide the new user to create their first novel.
  // SAFETY: if the user already has projects but none loaded (e.g. the
  // bootstrap API call failed on a backend blip), show a reconnect screen
  // rather than the new-user wizard — this prevents experienced writers
  // from being dropped into the "Your story begins here" onboarding page
  // simply because projectsApi.list() hiccuped for one cycle.
  if (!project) {
    if (projects && projects.length > 0) return <ReconnectScreen />;
    return <FirstNovelPrompt />;
  }

  // Expose project id on window for lightweight cross-component access
  // (e.g. WorkflowRibbon "?" button clearing the coach-dismiss key).
  window.__loom_project_id__ = project.id;

  return (
    <div
      data-loom-shell="true"
      className="flex h-full w-full flex-col select-none"
      onDragOver={(e) => {
        const hasFile = Array.from(e.dataTransfer?.items || []).some((i) => i.kind === 'file');
        if (!hasFile) return;
        e.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) setDragOver(false);
      }}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        const f = e.dataTransfer?.files?.[0];
        if (!f) return;
        const name = f.name.toLowerCase();
        if (!name.match(/\.(docx|txt|md|markdown)$/)) return;
        setAiImportOpen(true);
      }}
    >
      <TitleBar
        onAbout={() => open('about')}
        onSettings={() => open('settings')}
        onBuyTokens={() => open('tokens')}
      />
      {/* Info strip — moved from bottom; dismissible via × or Preferences → Appearance */}
      {statusBarVisible && (
        <StatusBar
          onBuyTokens={() => open('tokens')}
          onDismiss={() => {
            localStorage.setItem(STATUSBAR_LS_KEY, '0');
            window.dispatchEvent(new CustomEvent(STATUSBAR_EVENT));
          }}
        />
      )}
      {/* Feb-2026 — Stripe return success banner (slides in from top) */}
      <StripeReturnBanner
        info={stripeReturnBanner}
        onDismiss={() => setStripeReturnBanner(null)}
      />
      {/* iter55f56 — DemoBanner removed per user request; the trial
       * banner below is now the sole in-app upgrade nag. */}
      <AdminModeBanner />
      {/* Toggleable marketing/notice banners — hidden in Quiet Mode (Tools → Hide notices) */}
      {!bannersQuiet && <TrialBanner />}
      {!bannersQuiet && <BonusToolsBanner />}
      <FreeTierSunsetBanner />
      {/* Feb-2026 · Token usage nudge — always visible (functional, not marketing) */}
      <TokenNudgeBanner onBuyTokens={() => open('tokens')} />
      {!bannersQuiet && (
        <OpportunityBanner
          hidden={visible.marketing}
          onOpen={() => setVisible((current) => ({ ...current, marketing: true }))}
        />
      )}
      {/* Quiet Mode restore ribbon — only shown when notices are suppressed */}
      {bannersQuiet && (
        <div
          data-testid="banners-quiet-ribbon"
          className="flex items-center justify-between px-4 py-1 text-[11px] bg-muted/40 border-b border-border/40 text-muted-foreground"
        >
          <span>Notices are hidden · <kbd className="rounded bg-muted px-1 py-px font-mono text-[10px]">Tools</kbd> → Show notices to restore</span>
          <button
            data-testid="banners-quiet-restore-btn"
            onClick={() => {
              localStorage.setItem('loom_banners_quiet', '0');
              window.dispatchEvent(new CustomEvent('loom:banners-quiet'));
            }}
            className="ml-3 underline underline-offset-2 hover:text-foreground transition-colors"
          >
            Show now
          </button>
        </div>
      )}
      <MenuBar
        onNewProject={handleNewProject}
        onNewFromTemplate={() => open('templates')}
        onSettings={() => open('settings')}
        onOpenSettings={() => open('settings')}   /* Feb-2026 · running-cost-pill click */
        onAbout={() => open('about')}
        onBuyTokens={() => open('tokens')}
        onExport={() => open('export')}
        onSaveAs={() => open('saveAs')}
        onImportManuscript={() => open('importManuscript')}
        onImportSynopsis={() => open('importSynopsis')}
        onImportThreeActSynopsis={() => open('importThreeActSynopsis')}
        onGenerateSynopsisFromLogline={() => open('loglineToSynopsis')}
        onBuildSynopsis={() => open('buildSynopsis')}
        onPublishToBookstore={() => open('publish')}
        onMyRoyalties={() => open('royalties')}
        onIsbnManager={() => open('isbn')}
        onCanvaCover={() => open('canva')}
        onAudiobook={() => open('audiobook')}
        onNarrationStudio={() => open('narrationStudio')}
        onAccountConnections={() => open('connections')}
        onOpenStacks={() => window.location.assign('/stacks')}
        onOpenFunds={() => window.location.assign('/funds')}
        onHelp={() => open('help')}
        onContextHelp={() => setHelpDrawerOpen(true)}
        onCommunity={() => { window.location.href = '/community'; }}
        onBookCoaching={() => open('bookCoaching')}
        onRequestEditorReview={() => open('requestEditorReview')}
        onScriptConverter={() => open('scriptConverter')}
        onKdpMetadata={() => open('kdpMetadata')}
        visible={visible}
        togglePanel={togglePanel}
        simpleMode={simpleMode}
        onToggleSimpleMode={() => setSimpleMode(!simpleMode)}
        glowFileTrigger={glowFileTrigger}
        glowLoadManuscript={glowLoadManuscript}
        onFileMenuOpen={handleFileMenuOpen}
        onLoadManuscriptClicked={handleLoadManuscriptClicked}
      />
      {/* Guided-mode onboarding popup — anchored near File menu, amber accent */}
      <OnboardingFileMenuPopup
        open={isGlowActive && glowStep === 'popup'}
        onClose={() => advanceGlow('file-menu')}
        onShowLoadManuscript={() => advanceGlow('file-menu')}
      />
      {/* Empty-manuscript hint — points to File → Load Manuscript */}
      {showImportHint && (
        <div
          className="flex shrink-0 items-center gap-2 border-b border-amber-300/60 bg-amber-50/80 px-3 py-1 dark:border-amber-700/40 dark:bg-amber-950/30"
          data-testid="manuscript-load-hint"
          role="status"
        >
          {/* Arrow pointing up-left toward File menu */}
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" className="shrink-0 text-amber-600 dark:text-amber-400" aria-hidden="true">
            <path d="M2 12 L2 2 L12 2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M2 2 L6 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
          </svg>
          <span className="text-[11.5px] text-amber-800 dark:text-amber-300">
            No manuscript loaded — open <strong className="font-semibold">File → Load Manuscript</strong> to import your draft.
          </span>
          <button
            type="button"
            onClick={() => { dismissImportHint(); open('importManuscript'); }}
            className="ml-1 rounded-sm px-1.5 py-0.5 text-[11px] font-semibold text-amber-700 hover:bg-amber-200/60 dark:text-amber-400 dark:hover:bg-amber-800/30"
            data-testid="manuscript-load-hint-open"
          >
            Open now
          </button>
          <button
            type="button"
            onClick={dismissImportHint}
            className="ml-auto rounded-sm p-0.5 text-amber-600/70 hover:bg-amber-200/60 dark:text-amber-500 dark:hover:bg-amber-800/30"
            title="Dismiss"
            data-testid="manuscript-load-hint-dismiss"
          >
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">
              <path d="M2 2 L8 8 M8 2 L2 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </button>
        </div>
      )}
      {/* ── Workflow Ribbon (full-width, Three-Act navigator) ─────── */}
      <WorkflowRibbon />
      {/* ── Drift Detection Banner (shown post-commit when foundation changes) ── */}
      <DriftDetectionBanner />
      {/* ── Main row: sidebar + workspace ───────────────────────── */}
      <div className="flex flex-1 min-h-0 overflow-hidden">
        <ActivityBar
          visible={visible}
          togglePanel={togglePanel}
          onBuyTokens={() => open('tokens')}
          onSettings={() => open('settings')}
          onOpenTemplates={() => open('templates')}
          onOpenQuickstart={() => setQuickstartOpen(true)}
          quickstartOpen={quickstartOpen}
          onOpenBusiness={() => setBusinessCenterOpen(true)}
          onOpenHelp={() => setHelpDrawerOpen(true)}
        />
        <div className="flex flex-1 min-h-0 flex-col overflow-hidden">
          <Workspace visible={visible} togglePanel={togglePanel} simpleMode={simpleMode} />
        </div>
      </div>
      <CommandPaletteHint />

      <AppDialogs
        dialogs={dialogs}
        open={open}
        close={close}
        viewingReviewId={viewingReviewId}
        setViewingReviewId={setViewingReviewId}
        pendingSynopsisFile={pendingSynopsisFile}
        onDropFile={(file) => setPendingSynopsisFile(file)}
      />
      {/* iter55g24 — Global mode-switch orchestrator: pops the
       *  Synopsis Builder / Revision panel / World Builder dialog
       *  whenever the writer changes mode via the top menu. */}
      <ModeSwitchHandler
        onOutline={() => open('buildSynopsis')}
        onRevise={() => open('revisionMode')}
        onWorld={() => open('worldBuilder')}
      />
      {/* iter55f56 — First-login prompt-mode picker (Guided / Light /
       * Off) → kicks off the onboarding walkthrough for new users. */}
      <OnboardingPromptModeDialog />
      <GettingStartedWizard togglePanel={togglePanel} />
      {/* iter55z — Pinnable community chat dock (left/right/bottom). */}
      <ChatDock
        open={!!visible.community}
        onClose={() => setVisible((v) => ({ ...v, community: false }))}
      />
      {/* iter55f93 — Quickstart Guide: pinnable right-side coaching panel. */}
      <QuickstartGuide
        open={quickstartOpen}
        onClose={() => setQuickstartOpen(false)}
        handlers={{
          openDialog: (k) => open(k),
          togglePanel: (k, on) => setVisible((v) => ({ ...v, [k]: on ?? !v[k] })),
          navigate: (path) => nav(path),
        }}
      />
      {/* Feb-2026 · "The Process" — cinematic spotlight walkthrough,
          registered in lib/tours.js and launched via the Sparkles icon
          in ActivityBar; uses the same global <GuidedTour/> engine
          mounted in App.js, so no separate dialog is rendered here. */}
      <BusinessCenterDrawer
        open={businessCenterOpen}
        onOpenChange={setBusinessCenterOpen}
        projectId={project?.id || null}
      />
      {/* iter-feedback — floating suggestion-box widget (bottom-right) */}
      <SuggestionBoxWidget />
      {/* Save-work gate — shown before New Project when a project is open */}
      <SaveWorkDialog
        open={saveWorkOpen}
        onOpenChange={setSaveWorkOpen}
        project={project}
        onProceed={() => open('newProject')}
      />
      {/* iter-context-help — Context Help drawer (panel-aware tips) */}
      <ContextHelpDrawer open={helpDrawerOpen} onClose={() => setHelpDrawerOpen(false)} />
      {/* iter-milestones — silent milestone watcher; fires amber toasts */}
      <MilestoneToastEngine />
      {/* PLG — Onboarding Activation Wizard (auto-shows for new Pro/Studio users) */}
      <OnboardingActivationWizard />
      {/* PLG — Marketing Asset Generator (opened via event or menu) */}
      <MarketingAssetGenerator
        open={marketingAssetsOpen}
        onClose={() => setMarketingAssetsOpen(false)}
      />
      {/* Feb-2026 · Full-screen overlay at 100 % token cap */}
      <TokenCapGate />
      {/* Writing Coach — 8-phase guided card, auto-shows on blank projects */}
      <WritingCoachCard />
      <CreationJourneyCard />
      {/* Drag-drop AI manuscript import dialog */}
      <ManuscriptAiImportDialog
        open={aiImportOpen}
        onOpenChange={setAiImportOpen}
      />
      {/* Admin Project Picker — File → Open Admin Project… */}
      <AdminProjectPickerDialog
        open={adminPickerOpen}
        onOpenChange={setAdminPickerOpen}
      />
      {/* Rename Manuscript — modal dialog replacing the subtle TitleBar inline edit */}
      <RenameManuscriptDialog
        open={renameManuscriptOpen}
        onOpenChange={setRenameManuscriptOpen}
      />
      {/* Act I Commit Gate — shown first time writer clicks "Draft" in WorkflowRibbon */}
      <CommitGateDialog
        open={commitGateOpen}
        onOpenChange={setCommitGateOpen}
        onCommitAndDraft={() => setVisible((v) => ({ ...v, editor: true, home: false }))}
      />
      {/* Drag-drop overlay hint */}
      {dragOver && (
        <div className="pointer-events-none fixed inset-0 z-[200] flex flex-col items-center justify-center gap-3 bg-background/80 backdrop-blur-sm ring-4 ring-inset ring-primary/50">
          <div className="flex flex-col items-center gap-2 rounded-2xl border-2 border-dashed border-primary bg-primary/10 px-12 py-8 text-center">
            <svg className="h-12 w-12 text-primary" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
            </svg>
            <p className="text-[15px] font-bold text-foreground">Drop your manuscript here</p>
            <p className="text-[12px] text-muted-foreground">.docx · .txt · .md — AI will extract plot, characters & synopsis</p>
          </div>
        </div>
      )}
    </div>
  );
}
